import type { ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import type { Id } from '@/infra/id'
import { Icon } from '@/components/Icon'
import styles from './DocumentRequestDialog.module.css'
import { Button, RawButton } from '@/components/Button'

export interface DocumentRequestDialogProps {
  showing: boolean
  isSubmitPending: boolean
  onClose: () => unknown
  onSubmit: () => unknown
  children?: ReactNode
}

export function DocumentRequestDialog({
  showing,
  isSubmitPending,
  onClose,
  onSubmit,
  children,
}: DocumentRequestDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
      testId="document-request-dialog"
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>Report open items</ModalSheetTitle>
      </ModalSheetHeader>
      <ModalSheetContent>{children}</ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            onPress={onClose}
            isDisabled={isSubmitPending}
            testId="document-request-dialog-close-button"
          />
          <Button
            label="Report Open Items"
            onPress={onSubmit}
            isPending={isSubmitPending}
            variant="primary"
            testId="document-request-dialog-submit-button"
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

export interface DocumentRequestContentItem {
  id: Id
  name: string
  checked: boolean
  onToggle: () => unknown
}

export interface DocumentRequestContentProps {
  items: DocumentRequestContentItem[]
}

export function DocumentRequestContent({ items }: DocumentRequestContentProps) {
  return (
    <ModalSheetSection spaced>
      <div className={styles.listHeaderTitle}>Select all that apply</div>

      {items.map((t) => (
        <RawButton
          key={t.id}
          className={styles.itemCell}
          aria-pressed={t.checked}
          onPress={t.onToggle}
          testId={`document-request-dialog-checkbox-${t.id}`}
        >
          <Icon
            color="var(--theme-base)"
            name={t.checked ? 'checkbox' : 'checkbox-blank'}
            size={24}
          />
          <div className={styles.itemTitle}>{t.name}</div>
        </RawButton>
      ))}
    </ModalSheetSection>
  )
}
